import autoTable from 'jspdf-autotable';
import { formatDate, formatMoney } from 'lib/functions/utils';

import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { getShortString } from '../reportUtils/documentUtils';

export interface PaymentRequestPassiveExportRequest {
  id?: number;
  sourceId?: number;
  status: string;
  pendingAccounts: Array<{
    businessName: string;
    businessIdentifier: string;
    amount: number;
    folio?: string;
    category?: string;
    observation?: string;
    issuedDate?: Date | string;
    totalAmount?: number;
  }>;
  documentsApplied: Array<{
    amount: number;
    document?: {
      id?: string;
      folio?: string;
      business?: {
        identifier?: string;
        name?: string;
      };
    };
    paidAt?: Date | string;
    createdAt?: Date | string;
  }>;
  createdAt: Date | string;
  executedAt?: Date | string;
  showPendingAccounts?: boolean;
  showAppliedDocuments?: boolean;
  showPayouts?: boolean;
  showClientInfo?: boolean;
  showDates?: boolean;
  showAmounts?: boolean;
  observation?: string | null;
  showObservation?: boolean;
}

export interface PaymentRequestPassiveReportConfig extends PDFGeneratorConfig {
  selectedRequest: PaymentRequestPassiveExportRequest;
  payouts?: any;
}

export class PaymentRequestPassiveReportGenerator extends BasePDFGenerator {
  private selectedRequest: PaymentRequestPassiveExportRequest;
  private payouts?: any;

  constructor(config: PaymentRequestPassiveReportConfig) {
    super({ ...config, orientation: 'landscape' });
    this.selectedRequest = config.selectedRequest;
    this.payouts = config.payouts;
  }

  protected addSummarySection(): void {
    const selectedRequest = this.selectedRequest;
    const showClientInfo = selectedRequest.showClientInfo !== false;
    const showDates = selectedRequest.showDates !== false;
    const showAmounts = selectedRequest.showAmounts !== false;

    const businessName =
      selectedRequest.pendingAccounts?.[0]?.businessName || '-';
    const businessIdentifier =
      selectedRequest.pendingAccounts?.[0]?.businessIdentifier || '-';

    const amountPendingAccounts = selectedRequest.pendingAccounts.reduce(
      (acc: number, account: any) => acc + (account.amount || 0),
      0,
    );
    const amountAppliedDocuments = selectedRequest.documentsApplied.reduce(
      (acc: number, doc: any) => acc + (doc.amount || 0),
      0,
    );
    const totalToPay = amountPendingAccounts - amountAppliedDocuments;

    this.doc.setFontSize(10);
    const col1X = 10;
    const col3X = 210;
    const defaultX = 35;
    const initialY = 30;
    let currentY = initialY;

    // Columna 1
    if (showClientInfo) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Razón Social', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      const shortName = getShortString(businessName, 25);
      this.doc.text(`: ${shortName}`, col1X + defaultX, currentY);
      currentY += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Rut Cliente', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(`: ${businessIdentifier}`, col1X + defaultX, currentY);
      currentY += 5;
    }

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Estado', col1X, currentY);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`: ${selectedRequest.status}`, col1X + defaultX, currentY);
    currentY += 5;

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Nro. Solicitud', col1X, currentY);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${String(selectedRequest.id || selectedRequest.sourceId || '-')}`,
      col1X + defaultX,
      currentY,
    );
    currentY += 5;

    if (showDates) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Fecha de creación', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${formatDate(new Date(selectedRequest.createdAt))}`,
        col1X + defaultX,
        currentY,
      );
      currentY += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Fecha de operación', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${selectedRequest.executedAt ? formatDate(new Date(selectedRequest.executedAt)) : 'No ejecutado'}`,
        col1X + defaultX,
        currentY,
      );
    }

    // Columna 3 - Montos
    if (showAmounts) {
      const alignRight = (text: string, x: number, y: number) => {
        const textWidth = this.doc.getTextWidth(text);
        this.doc.text(':', x + 35 - 25, y);
        this.doc.text(text, x + 35 - textWidth, y);
      };

      let col3Y = initialY;
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Cuentas pendientes', col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(amountPendingAccounts)}`,
        col3X + defaultX,
        col3Y,
      );
      col3Y += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Documentos aplicados', col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(-amountAppliedDocuments)}`,
        col3X + defaultX,
        col3Y,
      );
      col3Y += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Total a pagar', col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      alignRight(`${formatMoney(totalToPay)}`, col3X + defaultX, col3Y);
    }
  }

  protected addContentSection(): void {
    if (this.selectedRequest.showPendingAccounts !== false) {
      this.addPendingAccountsTable();
    }
    if (this.selectedRequest.showAppliedDocuments !== false) {
      this.addAppliedDocumentsTable();
    }
  }

  private addPendingAccountsTable(): void {
    const selectedRequest = this.selectedRequest;
    if (
      !selectedRequest.pendingAccounts ||
      selectedRequest.pendingAccounts.length === 0
    ) {
      return;
    }

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Cuentas Pendientes', 10, 75);
    this.doc.setFont('helvetica', 'normal');

    autoTable(this.doc, {
      startY: 80,
      head: [
        [
          'Folio',
          'Categoría',
          'Observación',
          'Cliente',
          'Fecha',
          'Monto Total',
          'Monto Descontado',
        ],
      ],
      body: selectedRequest.pendingAccounts.map((account: any) => [
        String(account.folio || '-'),
        account.category || '-',
        account.observation || '-',
        getShortString(account.businessName || '-', 20),
        account.issuedDate ? formatDate(new Date(account.issuedDate)) : '-',
        formatMoney(account.totalAmount || 0),
        formatMoney(account.amount || 0),
      ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
    this.doc.setFontSize(10);
  }

  private addAppliedDocumentsTable(): void {
    const selectedRequest = this.selectedRequest;
    if (
      !selectedRequest.documentsApplied ||
      selectedRequest.documentsApplied.length === 0
    ) {
      return;
    }

    this.doc.setFontSize(10);
    const lastTable = (this.doc as any).lastAutoTable;
    const titleY = lastTable ? lastTable.finalY + 15 : 75;

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Documentos Aplicados', 10, titleY);
    this.doc.setFont('helvetica', 'normal');

    autoTable(this.doc, {
      startY: titleY + 5,
      head: [
        [
          'Nro. Doc',
          'Cliente',
          'Rut Cliente',
          'Monto',
          'Fecha Aplicación',
          'Fecha Pago',
        ],
      ],
      body: selectedRequest.documentsApplied.map((doc: any) => [
        String(doc.document?.folio || '-'),
        getShortString(doc.document?.business?.name || '-', 20),
        doc.document?.business?.identifier || '-',
        formatMoney(doc.amount || 0),
        doc.createdAt ? formatDate(new Date(doc.createdAt)) : '-',
        doc.paidAt ? formatDate(new Date(doc.paidAt)) : '-',
      ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
  }

  public async generate() {
    await this.addTitleSection(this.selectedRequest);
    this.addSummarySection();
    this.addContentSection();
    if (
      this.selectedRequest.showObservation !== false &&
      this.selectedRequest.observation
    ) {
      this.addObservationFooter(this.selectedRequest.observation);
    }
    if (this.payouts && this.selectedRequest.showPayouts !== false) {
      this.doc.addPage();
      await this.addPayoutsSection(this.payouts);
    }
    this.saveDocument();
  }

  public async addPayoutsSection(payoutGroup: any): Promise<void> {
    if (!payoutGroup || !Array.isArray(payoutGroup.payments)) return;
    await this.addTitleSectionCustom('Pagos Realizados');
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    const payments = payoutGroup.payments;
    autoTable(this.doc, {
      startY: 30,
      head: [
        [
          'Banco Origen',
          'N° Cuenta Origen',
          'Banco Destino',
          'N° Cuenta Destino',
          'RUT Destino',
          'Tipo Cuenta Destino',
          'Titular Cuenta Destino',
          'Giro',
          'Monto',
        ],
      ],
      body:
        payments.length > 0
          ? payments.map((p: any) => [
              p.bankAccountOriginName,
              p.bankAccountOriginNumber,
              p.bankAccountDestinationName,
              p.bankAccountDestinationNumber,
              p.bankAccountDestinationHolderIdentifier,
              p.bankAccountDestinationAccountType,
              p.bankAccountDestinationHolderName,
              p.executedAt ? formatDate(p.executedAt) : '',
              formatMoney(p.amount),
            ])
          : [
              [
                {
                  content: 'No hay pagos realizados',
                  colSpan: 9,
                  styles: { halign: 'center', fontStyle: 'italic' },
                },
              ],
            ],
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
  }

  private async addTitleSectionCustom(title: string): Promise<void> {
    const { userName, imageBase64, orientation } = this.config;

    const imgX = 10,
      imgY = 5,
      imgHeight = 15;
    const { width: naturalWidth, height: naturalHeight } =
      await this.getImageDimensionsFromBase64(imageBase64);
    const aspectRatio = naturalWidth / naturalHeight;
    const imgWidth = imgHeight * aspectRatio;
    this.doc.addImage(
      imageBase64 as any,
      'PNG',
      imgX,
      imgY,
      imgWidth,
      imgHeight,
    );
    const pageWidth = orientation === 'landscape' ? 297 : 210;
    this.drawText(title, pageWidth / 2, 15, {
      size: 16,
      align: 'center',
      bold: true,
    });
    const rightX = orientation === 'landscape' ? 230 : 150;
    this.drawText(`Fecha: ${new Date().toLocaleString()}`, rightX, 10, {
      size: 10,
    });
    this.drawText(`Usuario: ${userName}`, rightX, 15, { size: 10 });
  }

  private addObservationFooter(observation: string) {
    const pageHeight = this.doc.internal.pageSize.getHeight();
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'italic');
    // 10 puntos arriba del borde inferior
    this.doc.text(`*Observación: ${observation}`, 15, pageHeight - 10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
  }
}
